import { FC, memo, useCallback, useEffect, useMemo, useState } from "react";
import { Box, Typography } from "@material-ui/core";
import { usePlaidLink } from "react-plaid-link";

import { Button, ErrorMessage } from "components";
import { DEFAULT_ERROR_MESSAGE } from "services/constants";
import { getPlaidLink } from "services/users";

interface PlaidPayButtonProps {
  onSuccess: (publicToken: string) => void;
  onExit: () => void;
  submittingPayment: boolean;
  setSubmittingPayment: (v: boolean) => void;
}

const PlaidPayButton: FC<PlaidPayButtonProps> = memo(function PlaidPayButton({
  onSuccess,
  onExit,
  submittingPayment,
  setSubmittingPayment,
}) {
  const [linkToken, setLinkToken] = useState<string>("");
  const [errorMessage, setErrorMessage] = useState<string>("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = await getPlaidLink();
        if (!cancelled) {
          setLinkToken(token);
        }
      } catch (e) {
        setErrorMessage(e instanceof Error ? e.message : DEFAULT_ERROR_MESSAGE);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const config = useMemo(
    () => ({
      token: linkToken,
      onSuccess,
      onExit,
    }),
    [linkToken, onSuccess, onExit],
  );

  const { ready, error, open: openPlaidLink } = usePlaidLink(config);

  const handleClick = useCallback(() => {
    setSubmittingPayment(true);
    openPlaidLink();
  }, [openPlaidLink, setSubmittingPayment]);

  return (
    <>
      {errorMessage && (
        <Box my={2}>
          <ErrorMessage title="Request Failed" description={errorMessage} />
        </Box>
      )}
      <Box
        mt={2}
        mb={2}
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <Button
          fullWidth
          customColor="green"
          variant="contained"
          onClick={handleClick}
          disabled={!ready || !!error || !!submittingPayment}
          isLoading={submittingPayment}
        >
          Continue to Pay
        </Button>
      </Box>
      {error?.message && (
        <Typography align="center" color="error">
          {error.message}
        </Typography>
      )}
    </>
  );
});

export default PlaidPayButton;
